import { useEffect, useRef, useState } from 'react'
import { createProject, deleteProject, getProject, listProjects, saveOutline, saveScript, updateProject } from '../../api/projects'
import { getInspirations, getTopics, saveTopics, updateTopic } from '../../api/data'
import { ApiError } from '../../api/client'
import { streamStudioGeneration } from '../../api/agent'
import type { InspirationItem, LoadState, MaterialRef, Outline, Project, ProjectDetail, Script, StoredOutline, StoredScript, Topic } from '../../types/api'
import { PageHeader, StatusMessage } from '../../components/AppShell'
import { AssetPreview, ManualTopicModal, MaterialPicker, OutlineModal } from './StudioModals'
import { ScriptEditor } from './ScriptEditor'
import { Library, MyProjects, ProjectBuilder, ProjectWorkspace } from './StudioViews'
import { EMPTY_TOPIC, asTopicForm, creationPreview, emptyOutline, emptyScript, hasMeaningfulCreation, kindLabel, latestCreation, materialKey, mergeCreationPreservingDraft, outlineContext, topicPreview, type Kind, type PickerMode, type PickerState, type PreviewState, type StoredCreation, type Tab, type TopicForm } from './shared'

type StudioLocation = { projectId?: string; asset?: 'outline' | 'script'; builder?: boolean; view?: Tab; libraryKind?: Kind; panel?: 'picker' | 'preview'; previewKind?: Kind; previewId?: string; from?: Tab }
type ActiveStudioRequest = { id: number; projectId: string; kind: 'outline' | 'script' }

function readStudioLocation(): StudioLocation {
  const params = new URLSearchParams(location.hash.split('?')[1] || '')
  const asset = params.get('asset')
  const panel = params.get('panel')
  return {
    projectId: params.get('project') || undefined,
    asset: asset === 'outline' || asset === 'script' ? asset : undefined,
    builder: params.get('builder') === '1',
    view: params.get('view') === 'library' ? 'library' : 'projects',
    libraryKind: params.get('kind') === 'topic' || params.get('kind') === 'outline' || params.get('kind') === 'script' ? params.get('kind') as Kind : undefined,
    panel: panel === 'picker' || panel === 'preview' ? panel : undefined,
    previewKind: panel === 'preview' && (params.get('preview_kind') === 'topic' || params.get('preview_kind') === 'outline' || params.get('preview_kind') === 'script') ? params.get('preview_kind') as Kind : undefined,
    previewId: panel === 'preview' ? params.get('preview_id') || undefined : undefined,
    from: params.get('from') === 'library' ? 'library' : 'projects',
  }
}

function studioHash(value: StudioLocation) {
  const params = new URLSearchParams()
  if (value.projectId) params.set('project', value.projectId)
  if (value.asset) params.set('asset', value.asset)
  if (!value.projectId && value.builder) params.set('builder', '1')
  if (!value.projectId && !value.builder && value.view === 'library') params.set('view', 'library')
  if (!value.projectId && !value.builder && value.view === 'library' && value.libraryKind) params.set('kind', value.libraryKind)
  if (value.panel === 'picker') params.set('panel', 'picker')
  if (value.panel === 'preview') {
    params.set('panel', 'preview')
    if (value.previewKind) params.set('preview_kind', value.previewKind)
    if (value.previewId) params.set('preview_id', value.previewId)
  }
  if ((value.projectId || value.builder) && value.from === 'library') params.set('from', 'library')
  const query = params.toString()
  return query ? `#/studio?${query}` : '#/studio'
}

export function StudioWorkspacePage() {
  const [tab, setTab] = useState<Tab>(() => readStudioLocation().view || 'projects')
  const [state, setState] = useState<LoadState>('loading')
  const [projects, setProjects] = useState<Project[]>([])
  const [topics, setTopics] = useState<Topic[]>([])
  const [library, setLibrary] = useState<InspirationItem[]>([])
  const [selected, setSelected] = useState<ProjectDetail>()
  const [draft, setDraft] = useState<StoredCreation>()
  const [kind, setKind] = useState<'outline' | 'script'>('outline')
  const [builder, setBuilder] = useState(() => readStudioLocation().builder === true)
  const [builderTitle, setBuilderTitle] = useState('')
  const [builderMaterials, setBuilderMaterials] = useState<MaterialRef[]>([])
  const [picker, setPicker] = useState<PickerState>(null)
  const [pickerMaterials, setPickerMaterials] = useState<MaterialRef[]>([])
  const [manualTopicMode, setManualTopicMode] = useState<'builder' | 'project' | 'edit' | null>(null)
  const [editingTopicId, setEditingTopicId] = useState<string>()
  const [outlineModal, setOutlineModal] = useState(false)
  const [scriptEditor, setScriptEditor] = useState(false)
  const [preview, setPreview] = useState<PreviewState>()
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiState, setAiState] = useState<'idle' | 'loading' | 'ready' | 'partial' | 'unavailable' | 'error'>('idle')
  const [notice, setNotice] = useState('')
  const [savingBuilder, setSavingBuilder] = useState(false)
  const studioLocationRef = useRef<StudioLocation>(readStudioLocation())
  const studioRequestRef = useRef<ActiveStudioRequest | undefined>(undefined)
  const studioRequestSequenceRef = useRef(0)

  const refreshCollections = async () => {
    const [topicRows, inspirationRows] = await Promise.all([getTopics().catch(() => []), getInspirations().catch(() => [])])
    setTopics(topicRows || [])
    setLibrary(inspirationRows || [])
    return { topics: topicRows || [], library: inspirationRows || [] }
  }
  const load = async () => {
    setState('loading')
    try {
      const [projectRows, topicRows, inspirationRows] = await Promise.all([listProjects(), getTopics().catch(() => []), getInspirations().catch(() => [])])
      setProjects(projectRows || []); setTopics(topicRows || []); setLibrary(inspirationRows || []); setState('ready')
    } catch (error) { setState(error instanceof ApiError && error.unavailable ? 'unavailable' : 'error') }
  }
  useEffect(() => { void load(); const onLibrary = () => { setTab('library'); setBuilder(false); setSelected(undefined) }; const onProjects = () => { setTab('projects'); setBuilder(false); setSelected(undefined) }; window.addEventListener('biliup:studio-library', onLibrary); window.addEventListener('biliup:studio-projects', onProjects); return () => { window.removeEventListener('biliup:studio-library', onLibrary); window.removeEventListener('biliup:studio-projects', onProjects) } }, [])
  useEffect(() => { window.dispatchEvent(new CustomEvent('biliup:project-context', { detail: selected ? { projectId: selected.project_id, title: selected.title } : null })) }, [selected])
  useEffect(() => () => { window.dispatchEvent(new CustomEvent('biliup:project-context', { detail: null })) }, [])

  const updateStudioLocation = (next: StudioLocation, mode: 'push' | 'replace' = 'push', managed = mode === 'push') => {
    const nextHash = studioHash(next)
    studioLocationRef.current = next
    if (location.hash === nextHash) return
    if (mode === 'replace') window.history.replaceState({ ...(window.history.state || {}), biliupStudioManaged: managed }, '', nextHash)
    else {
      location.hash = nextHash
      window.history.replaceState({ ...(window.history.state || {}), biliupStudioManaged: managed }, '', location.href)
    }
  }
  const clearProjectView = (returnTab: Tab = 'projects') => {
    setSelected(undefined); setDraft(undefined); setScriptEditor(false); setOutlineModal(false); setPicker(null); setPreview(undefined); setManualTopicMode(null); setEditingTopicId(undefined); setBuilder(false); setAiPrompt(''); setAiState('idle'); setTab(returnTab)
  }
  const closeCreationEditor = () => {
    setAiPrompt(''); setAiState('idle')
    const current = readStudioLocation()
    if (current.projectId && current.asset && window.history.state?.biliupStudioManaged) { window.history.back(); return }
    if (current.projectId && current.asset) updateStudioLocation({ projectId: current.projectId, from: current.from }, 'replace')
    setOutlineModal(false); setScriptEditor(false)
  }
  const closePicker = () => {
    const current = readStudioLocation()
    if (current.panel === 'picker' && window.history.state?.biliupStudioManaged) { window.history.back(); return }
    if (current.panel === 'picker') updateStudioLocation({ ...current, panel: undefined }, 'replace')
    setPicker(null)
  }
  const closePreview = () => {
    const current = readStudioLocation()
    if (current.panel === 'preview' && window.history.state?.biliupStudioManaged) { window.history.back(); return }
    if (current.panel === 'preview') updateStudioLocation({ ...current, panel: undefined, previewKind: undefined, previewId: undefined }, 'replace')
    setPreview(undefined)
  }
  const leaveProject = () => {
    const current = readStudioLocation()
    if (current.projectId && window.history.state?.biliupStudioManaged) { window.history.back(); return }
    updateStudioLocation({}, 'replace')
    clearProjectView(current.from === 'library' ? 'library' : 'projects')
  }
  const closeBuilder = () => {
    const current = readStudioLocation()
    if (current.builder && window.history.state?.biliupStudioManaged) { window.history.back(); return }
    updateStudioLocation({}, 'replace')
    setBuilder(false); setTab(current.from === 'library' ? 'library' : 'projects')
  }
  const openLibrary = () => { updateStudioLocation({ view: 'library' }); setTab('library'); setBuilder(false); setSelected(undefined) }
  const closeLibrary = () => {
    if (readStudioLocation().view === 'library' && window.history.state?.biliupStudioManaged) { window.history.back(); return }
    updateStudioLocation({}, 'replace'); setTab('projects')
  }
  const openProject = async (project: Project, options: { syncUrl?: boolean; syncMode?: 'push' | 'replace'; from?: Tab } = {}) => {
    if (options.syncUrl !== false) updateStudioLocation({ projectId: project.project_id, from: options.from || 'projects' }, options.syncMode || 'push', true)
    try {
      const detail = await getProject(project.project_id)
      setSelected(detail); setBuilder(false); setTab('projects'); setAiPrompt(''); setAiState('idle'); setNotice('')
      const nextKind: 'outline' | 'script' = detail.scripts?.length ? 'script' : 'outline'
      setKind(nextKind); setDraft(latestCreation(detail, nextKind)); setScriptEditor(false)
    } catch (error) { setNotice(error instanceof Error ? error.message : '项目读取失败') }
  }
  useEffect(() => {
    if (selected) return
    const locationState = readStudioLocation()
    if (locationState.builder) {
      setBuilder(true); setTab(locationState.from || 'projects'); setBuilderTitle(''); setBuilderMaterials([])
      return
    }
    if (locationState.view === 'library') { setTab('library'); return }
    if (!projects.length) return
    const projectId = locationState.projectId
    const asset = locationState.asset
    const project = projectId ? projects.find(item => item.project_id === projectId) : undefined
    if (!project) return
    void openProject(project, { syncUrl: false, from: locationState.from }).then(() => {
      if (asset === 'script') { setKind('script'); setScriptEditor(true) }
      if (asset === 'outline') { setKind('outline'); setOutlineModal(true) }
    })
  }, [projects, selected])

  useEffect(() => {
    const onHashChange = () => {
      const next = readStudioLocation()
      const previous = studioLocationRef.current
      // updateStudioLocation() updates the ref before assigning location.hash.
      // Ignore the resulting event so our own navigation is not treated as a
      // second transition that clears the builder state.
      if (studioHash(next) === studioHash(previous)) return
      studioLocationRef.current = next
      if (!next.projectId) {
        if (next.builder) {
          if (!previous.builder) {
            clearProjectView(next.from === 'library' ? 'library' : 'projects')
            setBuilderTitle(''); setBuilderMaterials([])
          }
          setBuilder(true); setTab(next.from || 'projects')
        } else if (previous.projectId || previous.builder) clearProjectView(previous.from === 'library' ? 'library' : 'projects')
        else if (next.view !== previous.view) setTab(next.view || 'projects')
        if (next.panel !== previous.panel) { setPicker(next.panel === 'picker' ? { mode: next.projectId ? 'project' : 'builder' } : null); if (next.panel !== 'preview') setPreview(undefined) }
        return
      }
      if (next.projectId !== previous.projectId) {
        const project = projects.find(item => item.project_id === next.projectId)
        if (project) void openProject(project, { syncUrl: false, from: next.from }).then(() => {
          if (next.asset === 'script') { setKind('script'); setScriptEditor(true) }
          if (next.asset === 'outline') { setKind('outline'); setOutlineModal(true) }
        })
        return
      }
      if (next.asset !== previous.asset) {
        setOutlineModal(next.asset === 'outline')
        setScriptEditor(next.asset === 'script')
        if (next.asset) setKind(next.asset)
      }
      if (next.panel !== previous.panel) { setPicker(next.panel === 'picker' ? { mode: next.projectId ? 'project' : 'builder' } : null); if (next.panel !== 'preview') setPreview(undefined) }
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [projects])

  const startBuilder = (materials: MaterialRef[] = [], from: Tab = 'projects') => { updateStudioLocation({ builder: true, from }); setSelected(undefined); setBuilder(true); setBuilderTitle(''); setBuilderMaterials(materials); setTab('projects'); setNotice('') }
  const saveBuilder = async () => {
    const first = library.find(item => builderMaterials.some(ref => materialKey(ref) === materialKey(item)))
    const title = builderTitle.trim() || first?.title || '未命名视频项目'
    setSavingBuilder(true); setNotice('')
    try {
      const created = await createProject({ title, description: '', materials: builderMaterials })
      setProjects(current => [created, ...current]); setBuilder(false); await openProject(created, { from: 'projects', syncMode: 'replace' })
    } catch (error) { setNotice(error instanceof Error ? error.message : '项目创建失败') }
    finally { setSavingBuilder(false) }
  }
  const openPicker = (mode: PickerMode, pickerKind?: Kind) => {
    updateStudioLocation({ ...readStudioLocation(), panel: 'picker' })
    setPicker({ mode, kind: pickerKind })
    setPickerMaterials(mode === 'project' ? [...(selected?.materials || [])] : [...builderMaterials])
  }
  const updateSelectedMaterials = async (next: MaterialRef[]) => {
    if (!selected) return
    const hasTopic = next.some(ref => ref.kind === 'topic')
    try {
      const detail = await updateProject(selected.project_id, { materials: next, stage_status: { topic: hasTopic ? 'confirmed' : 'waiting' } }) as ProjectDetail
      setSelected(detail); setProjects(current => current.map(item => item.project_id === detail.project_id ? detail : item)); setNotice('')
    } catch (error) { setNotice(error instanceof Error ? error.message : '素材更新失败') }
  }
  const confirmPicker = async () => {
    if (!picker) return
    if (picker.mode === 'builder') { setBuilderMaterials(pickerMaterials); closePicker(); return }
    closePicker()
    await updateSelectedMaterials(pickerMaterials)
  }
  const persistManualTopic = async (value: TopicForm) => {
    const [created] = await saveTopics([{ title: value.title, summary: value.summary, angle: value.angle, fit: value.fit, evidence: [], reason_tips: [] }])
    if (!created) throw new Error('选题创建失败')
    await refreshCollections()
    return created
  }
  const closeTopicEditor = () => { setManualTopicMode(null); setEditingTopicId(undefined) }
  const submitManualTopic = async (value: TopicForm) => {
    if (manualTopicMode === 'edit' && editingTopicId) {
      const updated = await updateTopic(editingTopicId, value)
      setTopics(current => current.map(topic => topic.id === updated.id ? updated : topic))
      await refreshCollections()
      if (selected) {
        const detail = await getProject(selected.project_id)
        setSelected(detail)
        setProjects(current => current.map(item => item.project_id === detail.project_id ? detail : item))
      }
      setNotice('选题已保存。')
      closeTopicEditor()
      return
    }
    const created = await persistManualTopic(value)
    if (manualTopicMode === 'builder') {
      setBuilderMaterials(current => [{ kind: 'topic', id: created.id }, ...current.filter(ref => ref.kind !== 'topic')])
      setBuilderTitle(current => current || created.title)
    } else if (selected) {
      const next = [{ kind: 'topic' as const, id: created.id }, ...(selected.materials || []).filter(ref => ref.kind !== 'topic')]
      await updateSelectedMaterials(next)
    }
    closeTopicEditor()
  }
  const removeMaterial = async (ref: MaterialRef) => {
    if (!selected) return
    const next = (selected.materials || []).filter(item => materialKey(item) !== materialKey(ref))
    await updateSelectedMaterials(next)
  }
  const updateSelectedProject = async (patch: { title?: string; description?: string }) => {
    if (!selected) return
    try { const detail = await updateProject(selected.project_id, patch) as ProjectDetail; setSelected(detail); setProjects(current => current.map(item => item.project_id === detail.project_id ? detail : item)); setNotice('项目已保存。') }
    catch (error) { setNotice(error instanceof Error ? error.message : '项目保存失败'); throw error }
  }
  const openPreview = (item: InspirationItem, edit?: () => void) => {
    const current = readStudioLocation()
    updateStudioLocation({ ...current, panel: 'preview', previewKind: item.kind, previewId: item.id })
    setPreview({ item, edit })
  }
  const previewLibraryItem = (item: InspirationItem) => {
    const sourceTopic = item.kind === 'topic' ? topics.find(topic => topic.id === item.id) : undefined
    const previewItem = sourceTopic ? topicPreview(sourceTopic) : item
    const edit = item.kind === 'topic'
      ? () => { setEditingTopicId(item.id); setManualTopicMode('edit') }
      : item.project_id
        ? () => void editCreationAsset(item)
        : undefined
    openPreview(previewItem, edit)
  }
  const editCreationAsset = async (item: InspirationItem) => {
    if (!item.project_id || (item.kind !== 'outline' && item.kind !== 'script')) return
    try {
      const detail = await getProject(item.project_id)
      updateStudioLocation({ projectId: detail.project_id, asset: item.kind, from: 'library' })
      setSelected(detail); setBuilder(false); setTab('projects'); setNotice('')
      if (item.kind === 'outline') {
        setKind('outline'); setDraft(detail.outlines?.find(row => row.id === item.id) || latestCreation(detail, 'outline')); setOutlineModal(true)
      } else {
        setKind('script'); setDraft(detail.scripts?.find(row => row.id === item.id) || latestCreation(detail, 'script')); setScriptEditor(true)
      }
    } catch (error) { setNotice(error instanceof Error ? error.message : '素材编辑器打开失败') }
  }
  const openCreationEditor = (nextKind: 'outline' | 'script', nextDraft?: StoredCreation) => {
    if (!selected) return
    const current = readStudioLocation()
    updateStudioLocation({ projectId: selected.project_id, asset: nextKind, from: current.from })
    setKind(nextKind)
    setAiPrompt(''); setAiState('idle')
    if (nextDraft) setDraft(nextDraft)
    else if (nextKind === 'script') setDraft(latestCreation(selected, 'script') || ({ content: emptyScript(selected) } as StoredScript))
    if (nextKind === 'outline') setOutlineModal(true)
    else setScriptEditor(true)
  }
  const previewProjectMaterial = (ref: MaterialRef) => {
    if (!selected) return
    if (ref.kind === 'topic') {
      const topic = selected.topics?.find(item => item.id === ref.id) || topics.find(item => item.id === ref.id)
      if (topic) openPreview(topicPreview(topic), () => { setEditingTopicId(ref.id); setManualTopicMode('edit') })
      return
    }
    const stored = ref.kind === 'outline' ? selected.outlines?.find(item => item.id === ref.id) : selected.scripts?.find(item => item.id === ref.id)
    if (!stored) return
    const creationKind = ref.kind as 'outline' | 'script'
    openPreview(creationPreview(creationKind, stored), () => openCreationEditor(creationKind, stored))
  }
  const saveCurrentContent = async (contentOverride?: Outline | Script) => {
    if (!selected) return
    const currentContent = contentOverride || draft?.content || (kind === 'outline' ? emptyOutline(selected) : emptyScript(selected))
    const outline = currentContent as Outline
    const script = currentContent as Script
    const hasContent = kind === 'outline'
      ? Boolean(outline.hook.trim() || (Array.isArray(outline.tags) ? outline.tags.length : String(outline.tags || '').trim()) || outline.outline_text.trim() || outline.core_content.trim())
      : Boolean(script.script.trim())
    if (!hasContent) { setNotice(`当前${kindLabel(kind)}没有可保存内容；空产物不会写入工作区。`); return }
    try {
      const saved = kind === 'outline' ? await saveOutline(selected.project_id, currentContent as Outline) : await saveScript(selected.project_id, currentContent as Script)
      const materials = [...(selected.materials || []).filter(ref => materialKey(ref) !== materialKey({ kind, id: saved.id || '' })), ...(saved.id ? [{ kind, id: saved.id } as MaterialRef] : [])]
      await updateProject(selected.project_id, { materials, stage_status: { [kind]: 'confirmed' } })
      const detail = await getProject(selected.project_id)
      setSelected(detail); setProjects(current => current.map(item => item.project_id === detail.project_id ? detail : item)); setDraft(latestCreation(detail, kind)); setNotice(`${kindLabel(kind)}已保存并确认阶段。`); await refreshCollections()
    } catch (error) { setNotice(error instanceof Error ? error.message : '保存失败'); throw error }
  }
  const saveOutlineFromModal = async (saved: StoredOutline) => {
    if (!selected) return
    const content = saved.content
    if (!content || !(content.hook.trim() || (Array.isArray(content.tags) ? content.tags.length : String(content.tags || '').trim()) || content.outline_text.trim() || content.core_content.trim())) {
      setNotice('空大纲不会确认或写入项目。')
      return
    }
    const materials = [...(selected.materials || []).filter(ref => !(ref.kind === 'outline' && ref.id === saved.id)), ...(saved.id ? [{ kind: 'outline' as const, id: saved.id }] : [])]
    await updateProject(selected.project_id, { materials, stage_status: { outline: 'confirmed' } })
    const detail = await getProject(selected.project_id)
    setSelected(detail); setProjects(current => current.map(item => item.project_id === detail.project_id ? detail : item)); setKind('outline'); setDraft(detail.outlines?.slice(-1)[0]); closeCreationEditor(); setNotice('视频大纲已保存并确认。'); await refreshCollections()
  }
  const runAi = async () => {
    if (aiState === 'loading') return
    if (!selected) return
    const currentContent = draft?.content || (kind === 'outline' ? emptyOutline(selected) : emptyScript(selected))
    const hasReference = kind === 'outline'
      ? Boolean((currentContent as Outline).reference_topic)
      : Boolean((currentContent as Script).reference_outline)
    const hasDraft = kind === 'outline'
      ? Boolean((currentContent as Outline).title.trim() || (currentContent as Outline).hook.trim() || (currentContent as Outline).outline_text.trim() || (currentContent as Outline).core_content.trim())
      : Boolean((currentContent as Script).title.trim() || (currentContent as Script).script.trim())
    const hasContext = kind === 'outline'
      ? Boolean(aiPrompt.trim() || hasReference || hasDraft)
      : Boolean(aiPrompt.trim() || hasReference || hasDraft)
    if (!hasContext) { setNotice(kind === 'script' ? '请填写生成要求、选择参考大纲，或先写一点脚本草稿。' : '请填写生成要求、选择参考选题或先写一点大纲草稿。'); return }
    setAiState('loading'); setNotice('')
    const requestId = studioRequestSequenceRef.current + 1
    studioRequestSequenceRef.current = requestId
    const active: ActiveStudioRequest = { id: requestId, projectId: selected.project_id, kind }
    studioRequestRef.current = active
    const isVisibleRequest = () => studioRequestRef.current?.id === requestId && readStudioLocation().projectId === active.projectId && readStudioLocation().asset === active.kind
    const requestContent = kind === 'outline' ? outlineContext(currentContent as Partial<Outline>) : ({ title: (currentContent as Script).title, script: (currentContent as Script).script } as Script)
    const defaultUserMessage = kind === 'outline'
      ? (hasReference
        ? (hasDraft ? '请参考本次选择的选题，结合当前大纲草稿继续完善视频大纲。' : '请参考本次选择的选题，生成一份视频大纲。')
        : (hasDraft ? '请结合当前大纲草稿继续完善视频大纲。' : '请根据当前创作要求生成一份视频大纲。'))
      : (hasReference
        ? (hasDraft ? '请参考本次选择的大纲，结合当前脚本草稿继续生成或修改视频脚本。' : '请参考本次选择的大纲，生成一份视频脚本。')
        : (hasDraft ? '请结合当前脚本草稿继续生成或修改视频脚本。' : '请根据当前创作要求生成一份视频脚本。'))
    const studioRequestContext = kind === 'outline'
      ? { outline_draft: requestContent, ...(currentContent as Outline).reference_topic ? { reference_topic: (currentContent as Outline).reference_topic } : {} }
      : { script_draft: requestContent, ...(currentContent as Script).reference_outline ? { reference_outline: (currentContent as Script).reference_outline } : {} }
    let terminalStatus: string | undefined
    let generatedAny = false
    try {
      await streamStudioGeneration(kind, {
        operation: hasDraft ? 'revise' : 'generate',
        user_message: aiPrompt.trim() || defaultUserMessage,
        request_context: studioRequestContext,
      }, event => {
        if (!isVisibleRequest()) return
        const output = event.output && typeof event.output === 'object' ? event.output as Record<string, unknown> : undefined
        terminalStatus = typeof output?.status === 'string' ? output.status : typeof event.status === 'string' ? event.status : terminalStatus
        const generated = output?.kind === kind ? output : undefined
        if (generated && hasMeaningfulCreation(generated)) {
          generatedAny = true
          if (kind === 'outline') setDraft(current => ({ ...(current || {}), content: mergeCreationPreservingDraft(((current?.content || currentContent) as Outline) as unknown as Record<string, unknown>, generated) as unknown as Outline } as StoredOutline))
          else setDraft(current => ({ ...(current || {}), content: mergeCreationPreservingDraft(((current?.content || currentContent) as Script) as unknown as Record<string, unknown>, generated) as unknown as Script } as StoredScript))
        }
        if (event.message && !generated) setNotice(String(event.message))
      })
      if (!isVisibleRequest()) return
      if (terminalStatus === 'error' || terminalStatus === 'unavailable') throw new Error('AI 服务暂不可用，未生成内容。请检查配置后重试。')
      if (!generatedAny) { setAiState(terminalStatus === 'partial' ? 'partial' : 'unavailable'); setNotice(terminalStatus === 'partial' ? 'AI 仅返回部分结果，当前草稿已保留；请补充后重试。' : 'AI 未返回可用内容，当前草稿已保留；请调整要求后重试。'); return }
      setAiState(terminalStatus === 'partial' ? 'partial' : 'ready')
      if (terminalStatus === 'partial') setNotice('AI 仅返回部分结果，当前草稿已保留；请补充后再保存。')
    } catch (error) {
      if (!isVisibleRequest()) return
      setAiState(error instanceof Error && error.message.includes('暂不可用') ? 'unavailable' : 'error')
      setNotice(error instanceof Error ? error.message : 'AI 工作流不可用，当前草稿已保留。')
    } finally {
      if (studioRequestRef.current?.id === requestId) studioRequestRef.current = undefined
    }
  }
  const removeSelectedProject = async () => {
    if (!selected || !window.confirm(`确定删除「${selected.title}」？项目中的 Topic / Outline / Script 资源不会从素材库删除。`)) return
    try { await deleteProject(selected.project_id); setProjects(current => current.filter(item => item.project_id !== selected.project_id)); setSelected(undefined); setNotice('项目已删除。') }
    catch (error) { setNotice(error instanceof Error ? error.message : '删除项目失败') }
  }

  const topicEditor = manualTopicMode === 'edit' && editingTopicId ? <ManualTopicModal initial={asTopicForm(topics.find(topic => topic.id === editingTopicId))} title="编辑当前选题" onCancel={closeTopicEditor} onConfirm={submitManualTopic} /> : null

  if (state !== 'ready') return <><PageHeader eyebrow="CREATOR WORKSPACE" title="创作工坊" /><StatusMessage state={state === 'idle' ? 'loading' : state} onRetry={() => void load()} /></>
  if (builder) return <><ProjectBuilder title={builderTitle} setTitle={setBuilderTitle} materials={builderMaterials} library={library} onPicker={() => openPicker('builder')} onPreview={item => openPreview(item)} onRemove={ref => setBuilderMaterials(current => current.filter(item => materialKey(item) !== materialKey(ref)))} onManual={() => setManualTopicMode('builder')} onCancel={closeBuilder} onSave={() => void saveBuilder()} saving={savingBuilder} />{notice && <div className="inline-notice">{notice}</div>}{picker && <MaterialPicker items={library} selected={pickerMaterials} kind={picker.kind} onChange={setPickerMaterials} onPreview={previewLibraryItem} onManual={() => { closePicker(); setManualTopicMode('builder') }} onClose={closePicker} onConfirm={() => void confirmPicker()} />}{manualTopicMode === 'builder' && <ManualTopicModal onCancel={closeTopicEditor} onConfirm={submitManualTopic} />}{preview && <AssetPreview state={preview} onClose={closePreview} />}{topicEditor}</>
  if (selected && scriptEditor) return <ScriptEditor project={selected} content={(draft?.content || emptyScript(selected)) as Script} setContent={value => setDraft({ ...(draft || {}), content: value } as StoredScript)} aiPrompt={aiPrompt} setAiPrompt={setAiPrompt} aiState={aiState} notice={notice} runAi={() => void runAi()} save={value => saveCurrentContent(value)} onClose={closeCreationEditor} />
  if (selected) {
    const projectTopics = topics.filter(topic => (selected.materials || []).some(ref => ref.kind === 'topic' && ref.id === topic.id))
    const createMaterial = (stage: Kind) => {
      if (stage === 'outline') openCreationEditor('outline')
      else if (stage === 'script') openCreationEditor('script')
      else { setEditingTopicId(undefined); setManualTopicMode('project') }
    }
    return <><ProjectWorkspace project={selected} topics={topics} library={library} notice={notice} onUpdate={updateSelectedProject} onRemoveMaterial={removeMaterial} onAddMaterial={stage => openPicker('project', stage)} onCreateMaterial={createMaterial} onPreviewMaterial={previewProjectMaterial} onEditTopic={topicId => { setEditingTopicId(topicId); setManualTopicMode('edit') }} onDelete={() => void removeSelectedProject()} onBack={leaveProject} />{outlineModal && <OutlineModal project={selected} topics={projectTopics} onClose={closeCreationEditor} onSaved={saveOutlineFromModal} />}{picker && <MaterialPicker items={library} selected={pickerMaterials} kind={picker.kind} onChange={setPickerMaterials} onPreview={previewLibraryItem} onManual={() => { closePicker(); setManualTopicMode('project') }} onClose={closePicker} onConfirm={() => void confirmPicker()} />}{manualTopicMode === 'project' && <ManualTopicModal initial={EMPTY_TOPIC} title="手动填写选题" onCancel={closeTopicEditor} onConfirm={submitManualTopic} />}{preview && <AssetPreview state={preview} onClose={closePreview} />}{topicEditor}</>
  }
  return <>{tab === 'projects' && <MyProjects projects={projects} topics={topics} library={library} onNew={() => startBuilder()} onOpen={project => void openProject(project)} onOpenLibrary={openLibrary} />}{tab === 'library' && <Library items={library} initialKind={readStudioLocation().libraryKind} onProjects={closeLibrary} onNew={item => startBuilder([{ kind: item.kind, id: item.id }], 'library')} onOpen={previewLibraryItem} />}{preview && <AssetPreview state={preview} onClose={closePreview} />}{topicEditor}</>
}
